<script setup lang="ts">
import type { FundingDisplayDescriptor } from '~/utils/event-policy';
import { formatTimeRemaining } from '~/composables/format-duration';
import { formatCurrency } from '~/composables/format-currency';

defineProps<{
  funding: FundingDisplayDescriptor;
}>();
</script>

<template>
  <div v-if="funding.mode !== 'hidden'" class="event-funding-module">
    <!-- Fundraising: progress bar + deadline -->
    <template v-if="funding.mode === 'fundraising'">
      <v-progress-linear
        :model-value="Math.min(funding.progressPercentage, 100)"
        :color="funding.isGoalMet ? 'success' : 'primary'"
        rounded
        height="8"
        class="mb-2"
      />
      <div class="d-flex justify-space-between text-body-2 mb-1">
        <span>
          <template v-if="funding.isGoalMet && funding.committedCount !== null">
            <v-icon size="small" color="success" class="mr-1"
              >mdi-check-circle</v-icon
            >
            <template v-if="funding.thresholdType === 'cash'">
              Goal reached &mdash; {{ formatCurrency(funding.committedCount) }}
              raised
            </template>
            <template v-else-if="funding.thresholdType === 'attendees'">
              Goal reached &mdash; {{ funding.committedCount }}
              {{ funding.committedCount === 1 ? 'attendee' : 'attendees' }}
              committed
            </template>
            <template v-else>
              Goal reached &mdash; {{ funding.committedCount }}
              {{ funding.committedCount === 1 ? 'backer' : 'backers' }}
              committed
            </template>
          </template>
          <template v-else-if="funding.committedCount !== null">
            <template v-if="funding.thresholdType === 'cash'">
              {{ formatCurrency(funding.committedCount) }} of
              {{ formatCurrency(funding.threshold) }} raised
            </template>
            <template v-else-if="funding.thresholdType === 'attendees'">
              {{ funding.committedCount }} of {{ funding.threshold }}
              {{ funding.threshold === 1 ? 'attendee' : 'attendees' }}
            </template>
            <template v-else>
              {{ funding.committedCount }} of {{ funding.threshold }}
              {{ funding.threshold === 1 ? 'backer' : 'backers' }}
            </template>
          </template>
          <template v-else-if="funding.isGoalMet">
            <v-icon size="small" color="success" class="mr-1"
              >mdi-check-circle</v-icon
            >
            Goal reached
          </template>
          <template v-else> {{ funding.progressPercentage }}% funded </template>
        </span>
        <span v-if="funding.isSoldOut" class="text-error font-weight-medium">
          Sold out
        </span>
      </div>
      <div
        v-if="funding.timeRemaining"
        class="text-body-2 text-medium-emphasis"
      >
        {{ formatTimeRemaining(funding.timeRemaining) }}
      </div>
    </template>

    <!-- Finalization: indeterminate bar + processing message -->
    <template v-else-if="funding.mode === 'finalization'">
      <v-progress-linear
        indeterminate
        color="secondary"
        rounded
        height="8"
        class="mb-2"
      />
      <div class="text-body-2 font-weight-medium mb-1">
        Processing payments...
      </div>
      <div
        v-if="funding.finalizationCount !== null"
        class="text-body-2 text-medium-emphasis"
      >
        {{ funding.finalizationCount }} of
        {{ funding.committedCount ?? funding.threshold }}
        processed
      </div>
    </template>

    <!-- Post-confirmation: checkmark + status -->
    <template v-else-if="funding.mode === 'post_confirmation'">
      <div class="d-flex align-center text-body-2">
        <v-icon color="success" size="small" class="mr-2"
          >mdi-check-circle</v-icon
        >
        <span>
          <template v-if="funding.paidCount !== null">
            <template v-if="funding.thresholdType === 'cash'">
              {{ formatCurrency(funding.paidCount) }} in ticket sales
            </template>
            <template v-else-if="funding.thresholdType === 'attendees'">
              {{ funding.paidCount }}
              {{ funding.paidCount === 1 ? 'attendee' : 'attendees' }} confirmed
            </template>
            <template v-else> {{ funding.paidCount }} tickets sold </template>
          </template>
          <template v-else> Event confirmed </template>
        </span>
      </div>
    </template>

    <!-- Failed -->
    <template v-else-if="funding.mode === 'failed'">
      <v-alert type="error" variant="tonal" density="compact">
        {{ funding.message }}
      </v-alert>
    </template>

    <!-- Canceled -->
    <template v-else-if="funding.mode === 'canceled'">
      <v-alert type="error" variant="tonal" density="compact">
        {{ funding.message }}
      </v-alert>
    </template>

    <!-- Pre-fundraising -->
    <template v-else-if="funding.mode === 'pre_fundraising'">
      <div class="text-body-2 text-medium-emphasis">{{ funding.message }}</div>
    </template>
  </div>
</template>
